import React, { useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { v4 } from 'uuid';
import Pagination from '@prism/pagination';
import Icon$1, { Icon } from '@prism/icon';
import Button from '@prism/button';
import ButtonGroup from '@prism/buttongroup';
import { useClickAway } from 'react-use';
import { useTable, useSortBy, useExpanded, usePagination } from 'react-table';

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }

  return target;
}

function _typeof(obj) {
  "@babel/helpers - typeof";

  return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (obj) {
    return typeof obj;
  } : function (obj) {
    return obj && "function" == typeof Symbol && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj;
  }, _typeof(obj);
}

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function _extends() {
  _extends = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };
  return _extends.apply(this, arguments);
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};

  var target = _objectWithoutPropertiesLoose(source, excluded);

  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest();
}

function _toConsumableArray(arr) {
  return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _unsupportedIterableToArray(arr) || _nonIterableSpread();
}

function _arrayWithoutHoles(arr) {
  if (Array.isArray(arr)) return _arrayLikeToArray(arr);
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArray(iter) {
  if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter);
}

function _iterableToArrayLimit(arr, i) {
  var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];

  if (_i == null) return;
  var _arr = [];
  var _n = true;
  var _d = false;

  var _s, _e;

  try {
    for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i];

  return arr2;
}

function _nonIterableSpread() {
  throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

var _excluded$5 = ["condensed", "getTableProps", "headerStyle", "headerSticky", "responsive"];

var Table = function Table(props) {
  var _classNames;

  var condensed = props.condensed,
      getTableProps = props.getTableProps,
      headerStyle = props.headerStyle,
      headerSticky = props.headerSticky,
      responsive = props.responsive,
      attributes = _objectWithoutProperties(props, _excluded$5);

  var className = classNames((_classNames = {
    table: true,
    'table-hover': true,
    'table-sm': condensed
  }, _defineProperty(_classNames, "table-head-".concat(headerStyle), headerStyle), _defineProperty(_classNames, 'table-head-sticky', headerSticky), _defineProperty(_classNames, 'table-responsive', responsive), _classNames));
  return /*#__PURE__*/React.createElement("table", _extends({
    className: className
  }, getTableProps(), attributes));
};

Table.propTypes = {
  condensed: PropTypes.bool,
  getTableProps: PropTypes.func,
  headerSticky: PropTypes.bool,
  headerStyle: PropTypes.oneOf(['light', 'dark', 'color']),
  responsive: PropTypes.bool
};
Table.defaultProps = {
  condensed: false,
  getTableProps: function getTableProps() {},
  headerSticky: false,
  headerStyle: undefined,
  responsive: false
};
Table.displayName = 'Table';

var _excluded$4 = ["className", "headerLabel", "textAlign"];

var TableDataCell = function TableDataCell(props) {
  var _classNames;

  var className = props.className,
      headerLabel = props.headerLabel,
      textAlign = props.textAlign,
      attributes = _objectWithoutProperties(props, _excluded$4);

  var computedClassName = classNames((_classNames = {
    'table-data-cell': true
  }, _defineProperty(_classNames, className, className), _defineProperty(_classNames, "text-".concat(textAlign), textAlign), _classNames));
  return /*#__PURE__*/React.createElement("td", _extends({
    className: computedClassName,
    "data-heading": headerLabel
  }, attributes));
};

TableDataCell.propTypes = {
  className: PropTypes.string,
  headerLabel: PropTypes.node,
  textAlign: PropTypes.oneOf(['left', 'center', 'right'])
};
TableDataCell.defaultProps = {
  className: undefined,
  headerLabel: null,
  textAlign: undefined
};
TableDataCell.displayName = 'TableDataCell';

var TableRow = function TableRow(props) {
  var children = props.children,
      headerGroup = props.headerGroup,
      numVisibleColumns = props.numVisibleColumns,
      row = props.row;
  var rowProps = headerGroup && headerGroup.getHeaderGroupProps() || row && row.getRowProps() || {};
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("tr", _extends({
    className: "table-row"
  }, rowProps), children), row && row.isExpanded && row.original.expandedRow && /*#__PURE__*/React.createElement("tr", {
    className: "expanded-row"
  }, /*#__PURE__*/React.createElement("td", {
    colSpan: numVisibleColumns
  }, row.original.expandedRow)));
};

TableRow.propTypes = {
  children: PropTypes.node,
  headerGroup: PropTypes.shape({
    getHeaderGroupProps: PropTypes.func
  }),
  numVisibleColumns: PropTypes.number,
  row: PropTypes.shape({
    getRowProps: PropTypes.func,
    isExpanded: PropTypes.bool,
    original: PropTypes.shape({
      expandedRow: PropTypes.node
    })
  })
};
TableRow.defaultProps = {
  children: null,
  headerGroup: null,
  numVisibleColumns: undefined,
  row: undefined
};
TableRow.displayName = 'TableRow';

var TableBody = function TableBody(props) {
  var getTableBodyProps = props.getTableBodyProps,
      prepareRow = props.prepareRow,
      numVisibleColumns = props.numVisibleColumns,
      rows = props.rows;
  return /*#__PURE__*/React.createElement("tbody", _extends({
    className: "table-body"
  }, getTableBodyProps()), rows.map(function (row) {
    prepareRow(row);
    return /*#__PURE__*/React.createElement(TableRow, _extends({
      key: v4(),
      row: row,
      numVisibleColumns: numVisibleColumns
    }, row.getRowProps()), row.cells.map(function (cell) {
      var computedClassName = classNames(cell.column.className, {
        'row-expander': cell.column.id === 'expander'
      });
      return /*#__PURE__*/React.createElement(TableDataCell, _extends({
        key: v4(),
        className: computedClassName,
        headerLabel: cell.column.render('Header')
      }, cell.getCellProps()), cell.render('Cell'));
    }));
  }));
};

TableBody.propTypes = {
  getTableBodyProps: PropTypes.func,
  prepareRow: PropTypes.func,
  numVisibleColumns: PropTypes.number,
  rows: PropTypes.arrayOf(PropTypes.shape({
    getRowProps: PropTypes.func,
    cells: PropTypes.arrayOf(PropTypes.shape({
      column: PropTypes.shape({
        className: PropTypes.string,
        id: PropTypes.string,
        render: PropTypes.func
      }),
      getCellProps: PropTypes.func,
      render: PropTypes.func
    }))
  }))
};
TableBody.defaultProps = {
  getTableBodyProps: function getTableBodyProps() {},
  prepareRow: function prepareRow() {},
  numVisibleColumns: undefined,
  rows: []
};
TableBody.displayName = 'TableBody';

var TableBottom = function TableBottom(props) {
  var gotoPage = props.gotoPage,
      pagination = props.pagination;

  if (!pagination || pagination.pageCount < 2) {
    return null;
  }

  var handlePaginationChange = function handlePaginationChange(pageData) {
    gotoPage(pageData.index - 1 || 0);
  };

  var _pagination$pageIndex = pagination.pageIndex,
      pageIndex = _pagination$pageIndex === void 0 ? 0 : _pagination$pageIndex,
      pageCount = pagination.pageCount;
  var spacer = /*#__PURE__*/React.createElement("span", {
    style: {
      flexGrow: 1,
      height: 0
    }
  });
  return /*#__PURE__*/React.createElement("div", {
    className: "table-bottom"
  }, spacer, /*#__PURE__*/React.createElement(Pagination, {
    currentPage: pageIndex + 1,
    totalPages: pageCount,
    onChange: handlePaginationChange
  }), spacer);
};

TableBottom.propTypes = {
  gotoPage: PropTypes.func,
  pagination: PropTypes.oneOfType([PropTypes.bool, PropTypes.shape({
    pageIndex: PropTypes.number,
    pageCount: PropTypes.number
  })])
};
TableBottom.defaultProps = {
  gotoPage: function gotoPage() {},
  pagination: false
};
TableBottom.displayName = 'TableBottom';

var _excluded$3 = ["children", "contain", "headerStyle", "height", "isScrollable", "width"];

var TableContainer = function TableContainer(props) {
  var children = props.children,
      contain = props.contain;
      props.headerStyle;
      var height = props.height,
      isScrollable = props.isScrollable,
      width = props.width,
      attributes = _objectWithoutProperties(props, _excluded$3);

  return contain || isScrollable ? /*#__PURE__*/React.createElement("div", _extends({
    className: "table-container",
    style: {
      height: height,
      width: width
    }
  }, attributes), children) : /*#__PURE__*/React.createElement(React.Fragment, null, children);
};

TableContainer.propTypes = {
  children: PropTypes.node,
  contain: PropTypes.bool,
  headerStyle: PropTypes.string,
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  isScrollable: PropTypes.bool,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
};
TableContainer.defaultProps = {
  children: null,
  contain: false,
  headerStyle: undefined,
  height: undefined,
  isScrollable: false,
  width: '100%'
};
TableContainer.displayName = 'TableContainer';

var _excluded$2 = ["className"];

var TableFoot = function TableFoot(props) {
  var className = props.className,
      attributes = _objectWithoutProperties(props, _excluded$2);

  var computedClassName = classNames('table-foot', className);
  return /*#__PURE__*/React.createElement("tfoot", _extends({
    className: computedClassName
  }, attributes));
};

TableFoot.propTypes = {
  className: PropTypes.string
};
TableFoot.defaultProps = {
  className: ''
};
TableFoot.displayName = 'TableFoot';

var SortIcon = function SortIcon(_ref) {
  var isSorted = _ref.isSorted,
      isSortedDesc = _ref.isSortedDesc;
  var glyph;

  if (isSorted) {
    glyph = isSortedDesc ? 'arrow-down' : 'arrow-up';
  } else {
    glyph = 'arrows-up-down'; // off
  }

  var className = classNames({
    'table--sort-icon': true,
    'table--sort-icon--active': isSorted,
    'table--sort-icon--asc': isSorted && !isSortedDesc,
    'table--sort-icon--desc': isSorted && isSortedDesc
  });
  return /*#__PURE__*/React.createElement("span", {
    className: className
  }, /*#__PURE__*/React.createElement(Icon, {
    glyph: glyph
  }));
};

SortIcon.propTypes = {
  isSorted: PropTypes.bool,
  isSortedDesc: PropTypes.bool
};
SortIcon.defaultProps = {
  isSorted: false,
  isSortedDesc: false
};
SortIcon.displayName = 'SortIcon';

var _excluded$1 = ["canSort", "children", "className", "headerStyle", "isSorted", "isSortedDesc", "onAfterSort", "onBeforeSort", "onClick", "sortByProps", "textAlign", "toggleSortBy"];

var TableHeadCell = function TableHeadCell(props) {
  var _classNames;

  var canSort = props.canSort,
      children = props.children,
      className = props.className;
      props.headerStyle;
      var isSorted = props.isSorted,
      isSortedDesc = props.isSortedDesc,
      onAfterSort = props.onAfterSort,
      onBeforeSort = props.onBeforeSort,
      onClick = props.onClick,
      sortByProps = props.sortByProps;
      props.textAlign;
      props.toggleSortBy;
      var attributes = _objectWithoutProperties(props, _excluded$1);

  var handleClick = function handleClick(e) {
    e.preventDefault();

    if (canSort) {
      onBeforeSort(e);
      sortByProps === null || sortByProps === void 0 ? void 0 : sortByProps.onClick(e);
      onAfterSort(e);
    }

    onClick(e);
  };

  var computedClassName = classNames((_classNames = {
    'table-head-cell': true
  }, _defineProperty(_classNames, className, className), _defineProperty(_classNames, 'can-sort', canSort), _classNames));
  return /*#__PURE__*/React.createElement("th", _extends({}, attributes, {
    className: computedClassName,
    onClick: handleClick
  }), children, canSort && /*#__PURE__*/React.createElement(SortIcon, {
    isSorted: isSorted,
    isSortedDesc: isSortedDesc
  }));
};

TableHeadCell.propTypes = {
  canSort: PropTypes.bool,
  children: PropTypes.node,
  className: PropTypes.string,
  headerStyle: PropTypes.oneOf(['light', 'dark', 'color']),
  isSorted: PropTypes.bool,
  isSortedDesc: PropTypes.bool,
  onAfterSort: PropTypes.func,
  onBeforeSort: PropTypes.func,
  onClick: PropTypes.func,
  sortByProps: PropTypes.shape({
    onClick: PropTypes.func
  }),
  textAlign: PropTypes.oneOf(['left', 'right', 'center']),
  toggleSortBy: PropTypes.func
};
TableHeadCell.defaultProps = {
  canSort: false,
  children: null,
  className: undefined,
  headerStyle: undefined,
  isSorted: undefined,
  isSortedDesc: undefined,
  onAfterSort: function onAfterSort() {},
  onBeforeSort: function onBeforeSort() {},
  onClick: function onClick() {},
  sortByProps: null,
  textAlign: 'left',
  toggleSortBy: function toggleSortBy() {}
};
TableHeadCell.displayName = 'TableHeadCell';

var TableHead = function TableHead(_ref) {
  var headerGroups = _ref.headerGroups,
      singleSort = _ref.singleSort;
  return /*#__PURE__*/React.createElement("thead", {
    className: "table-head"
  }, headerGroups.map(function (headerGroup) {
    return /*#__PURE__*/React.createElement(TableRow, {
      key: v4(),
      headerGroup: headerGroup
    }, headerGroup.headers.map(function (column) {
      var canSort = column.canSort,
          getHeaderProps = column.getHeaderProps,
          getSortByToggleProps = column.getSortByToggleProps,
          headerClassName = column.headerClassName,
          isSorted = column.isSorted,
          isSortedDesc = column.isSortedDesc,
          onBeforeSort = column.onBeforeSort,
          onAfterSort = column.onAfterSort,
          render = column.render;
      return /*#__PURE__*/React.createElement(TableHeadCell, _extends({
        key: v4(),
        canSort: canSort && !singleSort,
        isSorted: canSort && isSorted,
        isSortedDesc: canSort && isSortedDesc,
        sortByProps: canSort ? getSortByToggleProps() : null,
        onBeforeSort: onBeforeSort,
        onAfterSort: onAfterSort,
        className: headerClassName
      }, getHeaderProps()), render('Header'));
    }));
  }));
};

TableHead.propTypes = {
  headerGroups: PropTypes.arrayOf(PropTypes.shape({
    headers: PropTypes.arrayOf(PropTypes.shape({
      canSort: PropTypes.bool,
      getHeaderProps: PropTypes.func,
      getSortByToggleProps: PropTypes.func,
      headerClassName: PropTypes.string,
      isSorted: PropTypes.bool,
      isSortedDesc: PropTypes.bool,
      onBeforeSort: PropTypes.func,
      onAfterSort: PropTypes.func,
      render: PropTypes.func
    }))
  })),
  singleSort: PropTypes.bool
};
TableHead.defaultProps = {
  headerGroups: [],
  singleSort: false
};
TableHead.displayName = 'TableHead';

var PageSize = function PageSize(_ref) {
  var label = _ref.label,
      pageSize = _ref.pageSize,
      pageSizes = _ref.pageSizes,
      setPageSize = _ref.setPageSize;
  return /*#__PURE__*/React.createElement("div", {
    className: "table-row-size"
  }, /*#__PURE__*/React.createElement("span", {
    className: "mr-2"
  }, label), /*#__PURE__*/React.createElement(ButtonGroup, null, pageSizes.map(function (size) {
    return /*#__PURE__*/React.createElement(Button, {
      key: size,
      variant: "outline",
      size: "sm",
      active: pageSize === size,
      onClick: function onClick() {
        return setPageSize(size);
      }
    }, size);
  })));
};

PageSize.propTypes = {
  label: PropTypes.string,
  pageSize: PropTypes.number,
  pageSizes: PropTypes.arrayOf(PropTypes.number),
  setPageSize: PropTypes.func
};
PageSize.defaultProps = {
  label: 'Results per page',
  pageSize: 25,
  pageSizes: [25, 50, 100],
  setPageSize: function setPageSize() {}
};
PageSize.displayName = 'PageSize';

var titleCase = function titleCase() {
  var str = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '';
  return str.replace(/\w\S*/g, function (txt) {
    return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
  });
};

var SingleSort = function SingleSort(props) {
  var columns = props.columns,
      label = props.label,
      setSortBy = props.setSortBy,
      sortBy = props.sortBy;

  var _React$useState = React.useState(false),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      sortOpen = _React$useState2[0],
      setSortOpen = _React$useState2[1];

  var popupRef = useRef();
  useClickAway(popupRef, function () {
    setSortOpen(false);
  });
  var sortableFields = columns.reduce(function (fields, col) {
    if (col.sortable && col.canSort) {
      fields.push({
        id: col.id,
        label: col.Header
      });
    }

    return fields;
  }, []);

  if (!sortableFields.length) {
    return null;
  }

  var handleSortClick = function handleSortClick(col, desc) {
    return function () {
      setSortOpen(false);
      setSortBy([{
        id: col,
        desc: !!desc
      }]);
    };
  };

  return /*#__PURE__*/React.createElement("div", {
    className: "table-single-sort sortControls popup-container",
    "aria-label": "single-sort"
  }, /*#__PURE__*/React.createElement("span", {
    className: "mr-2"
  }, label), /*#__PURE__*/React.createElement(Button, {
    variant: "actionLink",
    onClick: function onClick() {
      return setSortOpen(true);
    },
    "aria-label": "current-sort"
  }, sortBy.label || (columns.find(function (col) {
    return col.id === sortBy.id;
  }) || {}).Header || titleCase(sortBy.id), /*#__PURE__*/React.createElement("i", {
    className: "btn-icon__right icon prism-icon-arrow-".concat(sortBy.desc ? 'down' : 'up')
  })), /*#__PURE__*/React.createElement("div", {
    ref: popupRef,
    className: classNames('popup', sortOpen && 'open')
  }, sortableFields.map(function (field) {
    return /*#__PURE__*/React.createElement("div", {
      key: "table-sortby-".concat(field.id),
      className: "table-single-sort-row"
    }, /*#__PURE__*/React.createElement("span", {
      className: "table-single-sort-row-label"
    }, field.label), /*#__PURE__*/React.createElement(ButtonGroup, null, /*#__PURE__*/React.createElement(Button, {
      icononly: true,
      variant: "outline",
      size: "sm",
      active: sortBy.id === field.id && !sortBy.desc,
      onClick: handleSortClick(field.id),
      "aria-label": "".concat(field.label.toLowerCase().split(' ').join('-'), "-ascending-button")
    }, /*#__PURE__*/React.createElement(Icon, {
      glyph: "arrow-up"
    })), /*#__PURE__*/React.createElement(Button, {
      icononly: true,
      variant: "outline",
      size: "sm",
      active: sortBy.id === field.id && sortBy.desc,
      onClick: handleSortClick(field.id, true),
      "aria-label": "".concat(field.label.toLowerCase().split(' ').join('-'), "-descending-button")
    }, /*#__PURE__*/React.createElement(Icon, {
      glyph: "arrow-down"
    }))));
  })));
};

SingleSort.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.shape({
    canSort: PropTypes.bool,
    Header: PropTypes.string,
    id: PropTypes.string,
    sortable: PropTypes.bool
  })),
  label: PropTypes.string,
  setSortBy: PropTypes.func,
  sortBy: PropTypes.shape({
    desc: PropTypes.bool,
    id: PropTypes.string,
    label: PropTypes.string
  })
};
SingleSort.defaultProps = {
  columns: [],
  label: 'Sort by',
  setSortBy: function setSortBy() {},
  sortBy: {}
};

var TableTop = function TableTop(props) {
  var columns = props.columns,
      pagination = props.pagination,
      setSortBy = props.setSortBy,
      setPageSize = props.setPageSize,
      singleSort = props.singleSort,
      sortBy = props.sortBy;

  if (!singleSort && !pagination) {
    return null;
  }

  return /*#__PURE__*/React.createElement("div", {
    className: "table-top"
  }, pagination && /*#__PURE__*/React.createElement(PageSize, _extends({}, pagination, {
    setPageSize: setPageSize
  })), /*#__PURE__*/React.createElement("span", {
    style: {
      flexGrow: 2
    }
  }), singleSort && /*#__PURE__*/React.createElement(SingleSort, {
    columns: columns,
    sortBy: sortBy,
    setSortBy: setSortBy
  }));
};

TableTop.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.shape({
    canSort: PropTypes.bool,
    Header: PropTypes.string,
    id: PropTypes.string,
    sortable: PropTypes.bool
  })),
  pagination: PropTypes.oneOfType([PropTypes.bool, PropTypes.shape({
    label: PropTypes.string,
    pageIndex: PropTypes.number,
    pageSize: PropTypes.number
  })]),
  setSortBy: PropTypes.func,
  setPageSize: PropTypes.func,
  singleSort: PropTypes.bool,
  sortBy: PropTypes.shape({
    id: PropTypes.string,
    desc: PropTypes.bool,
    label: PropTypes.string
  })
};
TableTop.defaultProps = {
  columns: [],
  pagination: false,
  setSortBy: function setSortBy() {},
  setPageSize: function setPageSize() {},
  singleSort: false,
  sortBy: null
};
TableTop.displayName = 'TableTop';

/**
 *
 */

var columnFactory = function columnFactory() {
  var defaultColumnState = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return function (column) {
    var col = typeof column === 'string' ? {
      Header: column,
      accessor: column
    } : column;

    var __id = v4();

    var childSize = col.columns ? col.columns.length : 0;
    var onBeforeSort = 'onBeforeSort' in col ? col.onBeforeSort : defaultColumnState.onBeforeSort || null;
    var onAfterSort = 'onAfterSort' in col ? col.onAfterSort : defaultColumnState.onAfterSort || null;

    var resultColumn = _objectSpread2(_objectSpread2(_objectSpread2({}, defaultColumnState), {}, {
      id: col.accessor || __id,
      accessor: col.accessor || __id,
      Header: '',
      headers: childSize ? function () {
        return this.columns.map(function (child) {
          return child.ref.current && child.ref.current.id;
        }).join(' ');
      } : null,
      disableSortBy: 'sortable' in col ? !col.sortable : !defaultColumnState.sortable
    }, col), {}, {
      columns: Array.isArray(col.cols) ? col.columns.map(columnFactory) : null
    });

    resultColumn.onBeforeSort = onBeforeSort && function () {
      for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
        args[_key] = arguments[_key];
      }

      return onBeforeSort.apply(void 0, args.concat([resultColumn]));
    };

    resultColumn.onAfterSort = onAfterSort && function () {
      for (var _len2 = arguments.length, args = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
        args[_key2] = arguments[_key2];
      }

      return onAfterSort.apply(void 0, args.concat([resultColumn]));
    };

    return resultColumn;
  };
};

var findSortable = function findSortable() {
  var columns = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
  var sortable = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
  var canSort = null;

  if (columns.columns) {
    canSort = findSortable(columns.columns);
  } else {
    canSort = columns.find(function (c) {
      return _typeof(c) === 'object' && 'sortable' in c ? c.sortable : sortable;
    }) || canSort;
  }

  return canSort;
};

var useDataTable = function useDataTable(props) {
  var columns = props.columns,
      condensed = props.condensed,
      data = props.data,
      footer = props.footer,
      headerSticky = props.headerSticky,
      headerStyle = props.headerStyle,
      manualSortBy = props.manualSortBy,
      onAfterSort = props.onAfterSort,
      onBeforeSort = props.onBeforeSort,
      pagination = props.pagination,
      responsive = props.responsive,
      singleSort = props.singleSort,
      skipReset = props.skipReset,
      sortable = props.sortable,
      sortBy = props.sortBy;
  var canSort = findSortable(columns, sortable); // boolean or obj w/ `accessor` attr

  var isExpandableTable = data.some(function (row) {
    return row.expandedRow;
  }); // boolean

  var getExpanderCell = function getExpanderCell(row) {
    return /*#__PURE__*/React.createElement("span", row.getToggleRowExpandedProps(), /*#__PURE__*/React.createElement(Icon$1, {
      glyph: row.isExpanded ? 'chevron-down' : 'chevron-right',
      color: "secondary"
    }));
  };

  var computedColumns = useMemo(function () {
    var inputCols = columns.map(columnFactory({
      sortable: sortable,
      onBeforeSort: onBeforeSort,
      onAfterSort: onAfterSort
    }));

    if (isExpandableTable) {
      var expanderCol = {
        Header: '',
        id: 'expander',
        Cell: function Cell(_ref) {
          var row = _ref.row;
          return row.original.expandedRow ? getExpanderCell(row) : null;
        }
      };
      return [expanderCol].concat(_toConsumableArray(inputCols));
    }

    return inputCols;
  }, [columns]);
  var initialSortBy;

  if (sortBy.id) {
    initialSortBy = sortBy;
  } else {
    initialSortBy = canSort && canSort.accessor ? {
      sortBy: {
        id: canSort.accessor,
        desc: false
      }
    } : {};
  }

  var initialPagination = pagination ? {
    pageSize: pagination.pageSize || 25,
    pageIndex: pagination.pageIndex || 0
  } : {};

  var initialState = _objectSpread2({
    sortBy: [initialSortBy]
  }, initialPagination);

  var _useTable = useTable({
    columns: computedColumns,
    data: useMemo(function () {
      return data;
    }, [data]),
    defaultCanSort: false,
    disableSortRemove: true,
    manualSortBy: manualSortBy,
    initialState: initialState,
    autoResetPage: !skipReset,
    autoResetExpanded: !skipReset,
    autoResetGroupBy: !skipReset,
    autoResetSelectedRows: !skipReset,
    autoResetSortBy: !skipReset,
    autoResetFilters: !skipReset,
    autoResetRowState: !skipReset
  }, useSortBy, isExpandableTable && useExpanded, usePagination),
      flatHeaders = _useTable.flatHeaders,
      headerGroups = _useTable.headerGroups,
      getTableProps = _useTable.getTableProps,
      getTableBodyProps = _useTable.getTableBodyProps,
      gotoPage = _useTable.gotoPage,
      page = _useTable.page,
      pageCount = _useTable.pageCount,
      prepareRow = _useTable.prepareRow,
      rows = _useTable.rows,
      state = _useTable.state,
      setSortBy = _useTable.setSortBy,
      setPageSize = _useTable.setPageSize,
      visibleColumns = _useTable.visibleColumns;

  var tableTopProps = React.useMemo(function () {
    return {
      columns: flatHeaders,
      setSortBy: setSortBy,
      setPageSize: setPageSize,
      singleSort: singleSort,
      sortBy: state.sortBy[0] || {},
      pagination: pagination && {
        label: pagination.label,
        pageIndex: state.pageIndex,
        pageSize: state.pageSize,
        pageSizes: pagination.pageSizes
      }
    };
  }, [state.pageIndex, state.pageSize, state.sortBy, pagination]);
  var tableProps = {
    getTableProps: getTableProps,
    headerStyle: headerStyle,
    condensed: condensed,
    headerSticky: headerSticky,
    responsive: responsive
  };
  var tableHeadProps = {
    headerGroups: headerGroups,
    singleSort: singleSort
  };
  var tableBodyProps = {
    rows: pagination ? page : rows,
    getTableBodyProps: getTableBodyProps,
    prepareRow: prepareRow,
    numVisibleColumns: visibleColumns.length
  };
  var tableBottomProps = React.useMemo(function () {
    return {
      pagination: pagination && {
        pageIndex: state.pageIndex,
        pageCount: pageCount
      },
      gotoPage: gotoPage
    };
  }, [pagination, state.pageIndex, pageCount]);
  return {
    tableTopProps: tableTopProps,
    tableProps: tableProps,
    tableHeadProps: tableHeadProps,
    tableBodyProps: tableBodyProps,
    tableFootProps: _objectSpread2({}, footer),
    tableBottomProps: tableBottomProps
  };
};

var _excluded = ["caption", "contain", "height", "scrollable", "width"];

var DataTable = function DataTable(props) {
  var caption = props.caption,
      contain = props.contain,
      height = props.height,
      scrollable = props.scrollable,
      width = props.width,
      attributes = _objectWithoutProperties(props, _excluded);

  var _useDataTable = useDataTable(attributes),
      tableTopProps = _useDataTable.tableTopProps,
      tableProps = _useDataTable.tableProps,
      tableHeadProps = _useDataTable.tableHeadProps,
      tableBodyProps = _useDataTable.tableBodyProps,
      tableFootProps = _useDataTable.tableFootProps,
      tableBottomProps = _useDataTable.tableBottomProps;

  var containerProps = {
    contain: contain,
    height: height,
    isScrollable: scrollable,
    width: width
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(TableTop, tableTopProps), /*#__PURE__*/React.createElement(TableContainer, containerProps, caption && /*#__PURE__*/React.createElement("caption", null, caption), /*#__PURE__*/React.createElement(Table, tableProps, /*#__PURE__*/React.createElement(TableHead, tableHeadProps), /*#__PURE__*/React.createElement(TableBody, tableBodyProps), /*#__PURE__*/React.createElement(TableFoot, tableFootProps))), /*#__PURE__*/React.createElement(TableBottom, tableBottomProps));
};

DataTable.propTypes = {
  caption: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
  columns: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.object), PropTypes.arrayOf(PropTypes.string)]),
  condensed: PropTypes.bool,
  contain: PropTypes.bool,
  data: PropTypes.arrayOf(PropTypes.object),
  footer: PropTypes.shape({
    children: PropTypes.node,
    className: PropTypes.string
  }),
  headerSticky: PropTypes.bool,
  headerStyle: PropTypes.oneOf(['light', 'dark', 'color']),
  height: PropTypes.string,
  manualSortBy: PropTypes.bool,
  onAfterSort: PropTypes.func,
  onBeforeSort: PropTypes.func,
  pagination: PropTypes.oneOfType([PropTypes.bool, PropTypes.shape({
    label: PropTypes.string,
    pageIndex: PropTypes.number,
    pageSize: PropTypes.number,
    pageSizes: PropTypes.arrayOf(PropTypes.number)
  })]),
  responsive: PropTypes.bool,
  scrollable: PropTypes.bool,
  singleSort: PropTypes.bool,

  /**
   * If true, will disables some default behaviors in favor of more control from parent via props.
   * This is useful when updating data externally.
   * May cause unintended behavior if used incorrectly. Defaults to false.
   */
  skipReset: PropTypes.bool,
  sortable: PropTypes.bool,
  sortBy: PropTypes.shape({
    id: PropTypes.string,
    desc: PropTypes.bool
  }),
  width: PropTypes.string
};
DataTable.defaultProps = {
  caption: null,
  columns: [],
  condensed: false,
  contain: false,
  data: [],
  footer: null,
  headerSticky: false,
  headerStyle: undefined,
  height: undefined,
  manualSortBy: false,
  onAfterSort: function onAfterSort() {},
  onBeforeSort: function onBeforeSort() {},
  pagination: false,
  responsive: false,
  scrollable: false,
  singleSort: false,
  skipReset: false,
  sortable: false,
  sortBy: {},
  width: undefined
};
DataTable.displayName = 'DataTable';

DataTable.PageSize = PageSize;

export { PageSize, DataTable as default };
//# sourceMappingURL=index.js.map
